import { useState, useEffect, useCallback } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Badge } from './ui/badge';
import { Skeleton } from './ui/skeleton';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from './ui/dialog';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from './ui/table';
import {
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
} from './ui/tabs';
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  LineChart,
  Line,
  Legend,
  ComposedChart,
  Area,
  ReferenceLine
} from 'recharts';
import { 
  Users, DollarSign, TrendingUp, RefreshCw, ChevronRight,
  ArrowUpRight, ArrowDownRight, Target, AlertTriangle, Settings
} from 'lucide-react';
import { format } from 'date-fns';
import { usePermissions } from '../context/PermissionsContext';
import { toast } from 'sonner';
import { logger } from '../utils/logger';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Currency formatter - auto compact ($12.4k, $155k, $1.2M)
const formatCurrency = (value, compact = true) => {
  if (value === null || value === undefined) return '$0';
  const num = Number(value);
  if (!compact) return `$${num.toLocaleString()}`;
  
  if (num >= 1000000) return `$${(num / 1000000).toFixed(1)}M`;
  if (num >= 1000) return `$${(num / 1000).toFixed(1)}k`;
  return `$${num.toLocaleString()}`;
};

// Modern Custom Tooltip with rounded corners and shadow
const CustomTooltip = ({ active, payload, label, isCurrency = false }) => {
  if (active && payload && payload.length) {
    return (
      <div className="bg-white p-4 rounded-xl shadow-xl border border-slate-100" style={{ minWidth: '160px' }}>
        <p className="font-semibold text-slate-900 mb-2 text-sm">{label}</p>
        {payload.map((entry) => (
          <div key={entry.name} className="flex items-center justify-between gap-4 text-sm py-1">
            <span className="flex items-center gap-2">
              <span className="w-2.5 h-2.5 rounded-full" style={{ backgroundColor: entry.color }} />
              <span className="text-slate-600">{entry.name}</span>
            </span>
            <span className="font-semibold text-slate-900">
              {isCurrency || entry.name.toLowerCase().includes('$') || entry.name.toLowerCase().includes('value') || entry.name.toLowerCase().includes('collect')
                ? formatCurrency(entry.value, false)
                : entry.value?.toLocaleString()}
            </span>
          </div>
        ))}
        {payload[0]?.payload?.target && (
          <div className="flex items-center justify-between gap-4 text-sm py-1 border-t border-slate-100 mt-2 pt-2">
            <span className="flex items-center gap-2">
              <span className="w-2.5 h-2.5 rounded-full bg-red-500" />
              <span className="text-slate-600">Target</span>
            </span>
            <span className="font-semibold text-red-600">
              {isCurrency ? formatCurrency(payload[0].payload.target, false) : payload[0].payload.target}
            </span>
          </div>
        )}
      </div>
    );
  }
  return null;
};

// Executive KPI Tile
const ExecKPITile = ({ title, value, subtitle, icon: Icon, color = 'blue', trend, onClick }) => (
  <div 
    className={`p-4 rounded-xl border transition-all duration-200 ${onClick ? 'cursor-pointer hover:shadow-lg hover:-translate-y-0.5' : ''} ${
      color === 'blue' ? 'bg-blue-50/80 border-blue-200/60' :
      color === 'green' ? 'bg-emerald-50/80 border-emerald-200/60' :
      color === 'amber' ? 'bg-amber-50/80 border-amber-200/60' :
      color === 'red' ? 'bg-red-50/80 border-red-200/60' :
      'bg-slate-50/80 border-slate-200/60'
    }`}
    onClick={onClick}
    data-testid={`exec-kpi-${title.toLowerCase().replace(/\s+/g, '-')}`}
  >
    <div className="flex items-center justify-between">
      <div className="flex-1 min-w-0">
        <p className="text-[11px] font-semibold text-slate-500 uppercase tracking-wide truncate">{title}</p>
        <p className="text-xl font-bold text-slate-900 mt-1 truncate">{value}</p>
        {subtitle && <p className="text-xs text-slate-500 mt-0.5 truncate">{subtitle}</p>}
        {trend !== undefined && (
          <div className={`flex items-center gap-1 mt-1.5 text-xs font-semibold ${trend >= 0 ? 'text-emerald-600' : 'text-red-600'}`}>
            {trend >= 0 ? <ArrowUpRight size={12} /> : <ArrowDownRight size={12} />}
            {Math.abs(trend)}% vs last month
          </div>
        )}
      </div>
      {Icon && (
        <Icon className={`h-7 w-7 flex-shrink-0 ${
          color === 'blue' ? 'text-blue-500' :
          color === 'green' ? 'text-emerald-500' :
          color === 'amber' ? 'text-amber-500' :
          color === 'red' ? 'text-red-500' :
          'text-slate-500'
        }`} />
      )}
    </div>
    {onClick && (
      <div className="flex items-center gap-1 mt-2 text-xs text-slate-400">
        Click for details <ChevronRight size={12} />
      </div>
    )}
  </div>
);

// Drilldown Modal Component
const DrilldownModal = ({ isOpen, onClose, title, data, type, month }) => {
  const formatDate = (dateStr) => {
    try {
      return format(new Date(dateStr), 'MMM d, yyyy');
    } catch {
      return dateStr;
    }
  };

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="max-w-4xl max-h-[80vh] overflow-hidden flex flex-col">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            {title} 
            <Badge variant="outline">{month}</Badge>
          </DialogTitle>
        </DialogHeader>
        
        <div className="flex-1 overflow-y-auto mt-4">
          {type === 'conversions' && (
            <Table>
              <TableHeader>
                <TableRow className="bg-slate-50">
                  <TableHead>Lead Name</TableHead>
                  <TableHead>Email</TableHead>
                  <TableHead>Phone</TableHead>
                  <TableHead>Converted Date</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {data?.leads?.length > 0 ? data.leads.map((lead) => (
                  <TableRow key={lead.lead_id || `${lead.first_name}-${lead.phone}`} className="hover:bg-slate-50/50">
                    <TableCell className="font-medium">{lead.first_name} {lead.last_name}</TableCell>
                    <TableCell>{lead.email || '-'}</TableCell>
                    <TableCell>{lead.phone || '-'}</TableCell>
                    <TableCell>{formatDate(lead.updated_at)}</TableCell>
                  </TableRow>
                )) : (
                  <TableRow>
                    <TableCell colSpan={4} className="text-center text-slate-500 py-8">No conversions this month</TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          )}

          {type === 'cash' && (
            <>
              <div className="mb-4 p-4 bg-gradient-to-r from-emerald-50 to-emerald-100/50 rounded-xl border border-emerald-200/50">
                <p className="text-sm text-emerald-800">Total Collected: <span className="font-bold text-lg">{formatCurrency(data?.total, false)}</span></p>
              </div>
              <Table>
                <TableHeader>
                  <TableRow className="bg-slate-50">
                    <TableHead>Customer</TableHead>
                    <TableHead>Category</TableHead>
                    <TableHead>Amount</TableHead>
                    <TableHead>Payment Date</TableHead>
                    <TableHead>Receipt No.</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {data?.payments?.length > 0 ? data.payments.map((payment) => (
                    <TableRow key={payment.payment_id || `${payment.customer_name}-${payment.amount}`} className="hover:bg-slate-50/50">
                      <TableCell className="font-medium">{payment.customer_name}</TableCell>
                      <TableCell>
                        <Badge variant="outline" className="text-xs">{payment.payment_category}</Badge>
                      </TableCell>
                      <TableCell className="font-semibold text-emerald-600">{formatCurrency(payment.amount, false)}</TableCell>
                      <TableCell>{formatDate(payment.payment_date)}</TableCell>
                      <TableCell className="text-slate-500">{payment.money_receipt_no || '-'}</TableCell>
                    </TableRow>
                  )) : (
                    <TableRow>
                      <TableCell colSpan={5} className="text-center text-slate-500 py-8">No payments this month</TableCell>
                    </TableRow>
                  )}
                </TableBody>
              </Table>
            </>
          )}

          {type === 'sales' && (
            <>
              <div className="mb-4 p-4 bg-gradient-to-r from-blue-50 to-blue-100/50 rounded-xl border border-blue-200/50">
                <p className="text-sm text-blue-800">Total Sales Value: <span className="font-bold text-lg">{formatCurrency(data?.total, false)}</span></p>
              </div>
              <Table>
                <TableHeader>
                  <TableRow className="bg-slate-50">
                    <TableHead>Customer</TableHead>
                    <TableHead>Project</TableHead>
                    <TableHead>Contract Value</TableHead>
                    <TableHead>Created Date</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {data?.contracts?.length > 0 ? data.contracts.map((contract) => (
                    <TableRow key={contract.contract_id || `${contract.customer_name}-${contract.net_price}`} className="hover:bg-slate-50/50">
                      <TableCell className="font-medium">{contract.customer_name}</TableCell>
                      <TableCell>{contract.project_name || '-'}</TableCell>
                      <TableCell className="font-semibold text-blue-600">{formatCurrency(contract.net_price, false)}</TableCell>
                      <TableCell>{formatDate(contract.created_at)}</TableCell>
                    </TableRow>
                  )) : (
                    <TableRow>
                      <TableCell colSpan={4} className="text-center text-slate-500 py-8">No contracts this month</TableCell>
                    </TableRow>
                  )}
                </TableBody>
              </Table>
            </>
          )}

          {type === 'expected' && (
            <>
              <div className="mb-4 p-4 bg-gradient-to-r from-amber-50 to-amber-100/50 rounded-xl border border-amber-200/50">
                <p className="text-sm text-amber-800">Total Expected: <span className="font-bold text-lg">{formatCurrency(data?.total, false)}</span></p>
              </div>
              <Table>
                <TableHeader>
                  <TableRow className="bg-slate-50">
                    <TableHead>Customer</TableHead>
                    <TableHead>Installment #</TableHead>
                    <TableHead>Due Amount</TableHead>
                    <TableHead>Due Date</TableHead>
                    <TableHead>Status</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {data?.installments?.length > 0 ? data.installments.map((inst) => (
                    <TableRow key={inst.installment_id || `${inst.customer_name}-${inst.installment_number}`} className="hover:bg-slate-50/50">
                      <TableCell className="font-medium">{inst.customer_name}</TableCell>
                      <TableCell>{inst.installment_number}</TableCell>
                      <TableCell className="font-semibold">{formatCurrency(inst.due_amount, false)}</TableCell>
                      <TableCell>{formatDate(inst.due_date)}</TableCell>
                      <TableCell>
                        <Badge className={
                          inst.status === 'Paid' ? 'bg-green-100 text-green-800' :
                          inst.status === 'Overdue' ? 'bg-red-100 text-red-800' :
                          'bg-amber-100 text-amber-800'
                        }>{inst.status}</Badge>
                      </TableCell>
                    </TableRow>
                  )) : (
                    <TableRow>
                      <TableCell colSpan={5} className="text-center text-slate-500 py-8">No installments due this month</TableCell>
                    </TableRow>
                  )}
                </TableBody>
              </Table>
            </>
          )}

          {type === 'collected' && (
            <>
              <div className="mb-4 p-4 bg-gradient-to-r from-emerald-50 to-emerald-100/50 rounded-xl border border-emerald-200/50">
                <p className="text-sm text-emerald-800">Total Collected: <span className="font-bold text-lg">{formatCurrency(data?.total, false)}</span></p>
              </div>
              <Table>
                <TableHeader>
                  <TableRow className="bg-slate-50">
                    <TableHead>Customer</TableHead>
                    <TableHead>Amount</TableHead>
                    <TableHead>Payment Date</TableHead>
                    <TableHead>Method</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {data?.payments?.length > 0 ? data.payments.map((payment) => (
                    <TableRow key={payment.payment_id || `inst-${payment.customer_name}-${payment.amount}`} className="hover:bg-slate-50/50">
                      <TableCell className="font-medium">{payment.customer_name}</TableCell>
                      <TableCell className="font-semibold text-emerald-600">{formatCurrency(payment.amount, false)}</TableCell>
                      <TableCell>{formatDate(payment.payment_date)}</TableCell>
                      <TableCell className="text-slate-500">{payment.payment_method || '-'}</TableCell>
                    </TableRow>
                  )) : (
                    <TableRow>
                      <TableCell colSpan={4} className="text-center text-slate-500 py-8">No installment payments this month</TableCell>
                    </TableRow>
                  )}
                </TableBody>
              </Table>
            </>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
};

// Target Settings Modal
const TargetSettingsModal = ({ isOpen, onClose, onSaved }) => {
  const currentYear = new Date().getFullYear();
  const [selectedYear, setSelectedYear] = useState(currentYear);
  const [targets, setTargets] = useState({});
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);

  const months = [
    'January', 'February', 'March', 'April', 'May', 'June',
    'July', 'August', 'September', 'October', 'November', 'December'
  ];

  const targetTypes = [
    { key: 'client_conversions', label: 'Client Conversions', unit: 'count', color: 'blue' },
    { key: 'cash_collection', label: 'Cash Collection', unit: 'currency', color: 'emerald' },
    { key: 'sales_value', label: 'Sales Value', unit: 'currency', color: 'amber' },
    { key: 'installment_collection', label: 'Installment Collection', unit: 'currency', color: 'purple' },
  ];

  const fetchTargets = useCallback(async () => {
    setLoading(true);
    try {
      const response = await fetch(`${API_URL}/api/targets?year=${selectedYear}`, {
        credentials: 'include'
      });
      if (response.ok) {
        const data = await response.json();
        // Convert to lookup map
        const map = {};
        data.forEach(t => {
          const key = `${t.target_type}_${t.month}`;
          map[key] = t.target_value;
        });
        setTargets(map);
      }
    } catch (error) {
      logger.error('Error fetching targets:', error);
    } finally {
      setLoading(false);
    }
  }, [selectedYear]);

  useEffect(() => {
    if (isOpen) {
      fetchTargets();
    }
  }, [isOpen, fetchTargets]); // eslint-disable-line react-hooks/exhaustive-deps

  const handleTargetChange = (targetType, month, value) => {
    const key = `${targetType}_${month}`;
    setTargets(prev => ({
      ...prev,
      [key]: value === '' ? null : Number(value)
    }));
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      // Build list of targets to save
      const targetsToSave = [];
      targetTypes.forEach(tt => {
        for (let month = 1; month <= 12; month++) {
          const key = `${tt.key}_${month}`;
          if (targets[key] !== null && targets[key] !== undefined && targets[key] !== '') {
            targetsToSave.push({
              target_type: tt.key,
              year: selectedYear,
              month: month,
              target_value: Number(targets[key])
            });
          }
        }
      });

      const response = await fetch(`${API_URL}/api/targets/bulk`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify(targetsToSave)
      });

      if (response.ok) {
        const result = await response.json();
        toast.success(`Targets saved: ${result.created} created, ${result.updated} updated`);
        onSaved?.();
        onClose();
      } else {
        const error = await response.json();
        toast.error(error.detail || 'Failed to save targets');
      }
    } catch (error) {
      logger.error('Error saving targets:', error);
      toast.error('Failed to save targets');
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="max-w-6xl max-h-[90vh] overflow-hidden flex flex-col">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Target className="h-5 w-5 text-amber-500" />
            Set Targets for {selectedYear}
          </DialogTitle>
        </DialogHeader>

        <div className="flex items-center gap-4 py-3 border-b">
          <Label>Year:</Label>
          <select 
            value={selectedYear}
            onChange={(e) => setSelectedYear(Number(e.target.value))}
            className="px-3 py-2 border rounded-lg bg-white"
          >
            {[currentYear - 1, currentYear, currentYear + 1].map(y => (
              <option key={y} value={y}>{y}</option>
            ))}
          </select>
        </div>

        <div className="flex-1 overflow-auto py-4">
          {loading ? (
            <div className="flex items-center justify-center h-64">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-amber-500" />
            </div>
          ) : (
            <Tabs defaultValue="client_conversions" className="w-full">
              <TabsList className="grid grid-cols-4 w-full mb-4">
                {targetTypes.map(tt => (
                  <TabsTrigger key={tt.key} value={tt.key} className="text-xs">
                    {tt.label}
                  </TabsTrigger>
                ))}
              </TabsList>

              {targetTypes.map(tt => (
                <TabsContent key={tt.key} value={tt.key}>
                  <div className="grid grid-cols-3 md:grid-cols-4 lg:grid-cols-6 gap-3">
                    {months.map((monthName, idx) => {
                      const monthNum = idx + 1;
                      const key = `${tt.key}_${monthNum}`;
                      return (
                        <div key={monthNum} className="space-y-1">
                          <Label className="text-xs text-slate-500">{monthName}</Label>
                          <div className="relative">
                            {tt.unit === 'currency' && (
                              <span className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400">$</span>
                            )}
                            <Input
                              type="number"
                              value={targets[key] ?? ''}
                              onChange={(e) => handleTargetChange(tt.key, monthNum, e.target.value)}
                              placeholder="0"
                              className={`${tt.unit === 'currency' ? 'pl-7' : ''} h-9 text-sm`}
                            />
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </TabsContent>
              ))}
            </Tabs>
          )}
        </div>

        <DialogFooter className="border-t pt-4">
          <Button variant="outline" onClick={onClose}>Cancel</Button>
          <Button onClick={handleSave} disabled={saving} className="bg-amber-500 hover:bg-amber-600">
            {saving ? 'Saving...' : 'Save Targets'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

// Main Executive Charts Component for Dashboard
export function ExecutiveDashboardCharts() {
  const { role } = usePermissions();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [drilldown, setDrilldown] = useState({ open: false, type: '', month: '', data: null });
  const [loadingDrilldown, setLoadingDrilldown] = useState(false);
  const [showTargetSettings, setShowTargetSettings] = useState(false);

  const canSetTargets = ['admin', 'owner', 'top_management'].includes(role);

  const fetchData = useCallback(async () => {
    try {
      const response = await fetch(`${API_URL}/api/dashboard/executive-charts?months=12`, {
        credentials: 'include'
      });
      if (response.ok) {
        const result = await response.json();
        setData(result);
      }
    } catch (error) {
      logger.error('Error fetching executive charts:', error);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchData();
    // Real-time refresh every 60 seconds
    const interval = setInterval(fetchData, 60000);
    return () => clearInterval(interval);
  }, [fetchData]); // eslint-disable-line react-hooks/exhaustive-deps

  const openDrilldown = async (type, month) => {
    setLoadingDrilldown(true);
    try {
      let endpoint = '';
      if (type === 'conversions') endpoint = `/api/dashboard/drilldown/conversions/${month}`;
      else if (type === 'cash') endpoint = `/api/dashboard/drilldown/cash-collection/${month}`;
      else if (type === 'sales') endpoint = `/api/dashboard/drilldown/sales-value/${month}`;

      const response = await fetch(`${API_URL}${endpoint}`, { credentials: 'include' });
      if (response.ok) {
        const result = await response.json();
        setDrilldown({ open: true, type, month, data: result });
      }
    } catch (error) {
      logger.error('Error fetching drilldown:', error);
    } finally {
      setLoadingDrilldown(false);
    }
  };

  if (loading) {
    return (
      <div className="space-y-6">
        <Skeleton className="h-32 w-full rounded-xl" />
        <Skeleton className="h-80 w-full rounded-xl" />
        <Skeleton className="h-80 w-full rounded-xl" />
      </div>
    );
  }

  if (!data) return null;

  // Calculate totals for KPIs
  const totalConversions = data.client_conversions?.reduce((sum, d) => sum + d.value, 0) || 0;
  const totalCash = data.cash_collection?.reduce((sum, d) => sum + d.value, 0) || 0;
  const totalSales = data.sales_value?.reduce((sum, d) => sum + d.value, 0) || 0;

  // Calculate vs targets (if any targets exist)
  const currentMonthConversions = data.client_conversions?.[data.client_conversions.length - 1];
  const currentMonthCash = data.cash_collection?.[data.cash_collection.length - 1];
  const currentMonthSales = data.sales_value?.[data.sales_value.length - 1];

  return (
    <div className="space-y-6" data-testid="executive-charts">
      {/* Header with Target Settings */}
      <div className="flex items-center justify-between">
        <h2 className="text-lg font-semibold text-slate-900">Executive Performance Trends</h2>
        <div className="flex items-center gap-2">
          {canSetTargets && (
            <Button 
              variant="outline" 
              size="sm" 
              onClick={() => setShowTargetSettings(true)}
              className="text-slate-600"
              data-testid="set-targets-btn"
            >
              <Settings size={14} className="mr-1.5" />
              Set Targets
            </Button>
          )}
          <Button 
            variant="ghost" 
            size="sm" 
            onClick={fetchData}
            className="text-slate-500"
          >
            <RefreshCw size={14} className="mr-1" />
            Refresh
          </Button>
        </div>
      </div>

      {/* KPI Summary Row */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <ExecKPITile 
          title="Total Conversions (12mo)"
          value={totalConversions.toLocaleString()}
          subtitle={currentMonthConversions?.target ? `Target: ${currentMonthConversions.target}` : "Leads → Closed Won"}
          icon={Users}
          color="blue"
        />
        <ExecKPITile 
          title="Total Cash Collected (12mo)"
          value={formatCurrency(totalCash)}
          subtitle={currentMonthCash?.target ? `Target: ${formatCurrency(currentMonthCash.target)}` : "All payments received"}
          icon={DollarSign}
          color="green"
        />
        <ExecKPITile 
          title="Total Sales Value (12mo)"
          value={formatCurrency(totalSales)}
          subtitle={currentMonthSales?.target ? `Target: ${formatCurrency(currentMonthSales.target)}` : "Contract receivables"}
          icon={Target}
          color="amber"
        />
      </div>

      {/* Client Conversions Chart */}
      <Card className="shadow-sm border border-slate-200 rounded-2xl overflow-hidden">
        <CardHeader className="border-b bg-gradient-to-r from-blue-50/80 to-white py-4">
          <CardTitle className="flex items-center gap-2 text-base font-semibold">
            <Users className="h-5 w-5 text-blue-600" />
            Client Conversions (Monthly)
            <Badge variant="outline" className="ml-auto text-xs font-normal">Click bar for details</Badge>
          </CardTitle>
        </CardHeader>
        <CardContent className="p-4 pt-6">
          <ResponsiveContainer width="100%" height={280}>
            <ComposedChart 
              data={data.client_conversions} 
              margin={{ left: 20, right: 20, top: 20, bottom: 20 }}
            >
              <CartesianGrid strokeDasharray="3 3" stroke="#f1f5f9" strokeOpacity={0.8} />
              <XAxis 
                dataKey="label" 
                tick={{ fontSize: 11, fill: '#64748b' }} 
                tickLine={false}
                axisLine={{ stroke: '#e2e8f0' }}
              />
              <YAxis 
                tick={{ fontSize: 11, fill: '#64748b' }} 
                tickLine={false}
                axisLine={false}
                width={40}
              />
              <Tooltip content={<CustomTooltip />} />
              <Bar 
                dataKey="value" 
                fill="#3B82F6" 
                radius={[6, 6, 0, 0]}
                cursor="pointer"
                onClick={(data) => data && openDrilldown('conversions', data.month)}
                name="Conversions"
              />
              {data.client_conversions?.some(d => d.target) && (
                <Line 
                  type="monotone" 
                  dataKey="target" 
                  stroke="#EF4444" 
                  strokeWidth={2}
                  strokeDasharray="5 5"
                  dot={false}
                  name="Target"
                />
              )}
            </ComposedChart>
          </ResponsiveContainer>
          <div className="flex items-center justify-center gap-6 mt-2 text-xs text-slate-500">
            <span className="flex items-center gap-1.5">
              <span className="w-3 h-3 rounded bg-blue-500" /> Actual
            </span>
            {data.client_conversions?.some(d => d.target) && (
              <span className="flex items-center gap-1.5">
                <span className="w-4 h-0.5 bg-red-500" style={{ borderTop: '2px dashed #EF4444' }} /> Target
              </span>
            )}
          </div>
        </CardContent>
      </Card>

      {/* Cash Collection Chart */}
      <Card className="shadow-sm border border-slate-200 rounded-2xl overflow-hidden">
        <CardHeader className="border-b bg-gradient-to-r from-emerald-50/80 to-white py-4">
          <CardTitle className="flex items-center gap-2 text-base font-semibold">
            <DollarSign className="h-5 w-5 text-emerald-600" />
            Cash Collection (Monthly)
            <Badge variant="secondary" className="ml-auto text-xs bg-emerald-100 text-emerald-700 font-normal">
              Booking + Downpayment + Installments
            </Badge>
          </CardTitle>
        </CardHeader>
        <CardContent className="p-4 pt-6">
          <ResponsiveContainer width="100%" height={280}>
            <ComposedChart 
              data={data.cash_collection} 
              margin={{ left: 25, right: 20, top: 20, bottom: 20 }}
            >
              <CartesianGrid strokeDasharray="3 3" stroke="#f1f5f9" strokeOpacity={0.8} />
              <XAxis 
                dataKey="label" 
                tick={{ fontSize: 11, fill: '#64748b' }} 
                tickLine={false}
                axisLine={{ stroke: '#e2e8f0' }}
              />
              <YAxis 
                tick={{ fontSize: 11, fill: '#64748b' }} 
                tickFormatter={(v) => formatCurrency(v)}
                tickLine={false}
                axisLine={false}
                width={55}
              />
              <Tooltip content={<CustomTooltip isCurrency />} />
              <Area 
                type="monotone" 
                dataKey="value" 
                fill="#10B98115" 
                stroke="none"
              />
              <Bar 
                dataKey="value" 
                fill="#10B981" 
                radius={[6, 6, 0, 0]}
                cursor="pointer"
                onClick={(data) => data && openDrilldown('cash', data.month)}
                name="$ Collected"
              />
              {data.cash_collection?.some(d => d.target) && (
                <Line 
                  type="monotone" 
                  dataKey="target" 
                  stroke="#EF4444" 
                  strokeWidth={2}
                  strokeDasharray="5 5"
                  dot={false}
                  name="Target"
                />
              )}
            </ComposedChart>
          </ResponsiveContainer>
          <div className="flex items-center justify-center gap-6 mt-2 text-xs text-slate-500">
            <span className="flex items-center gap-1.5">
              <span className="w-3 h-3 rounded bg-emerald-500" /> Cash Collected
            </span>
            {data.cash_collection?.some(d => d.target) && (
              <span className="flex items-center gap-1.5">
                <span className="w-4 h-0.5 bg-red-500" style={{ borderTop: '2px dashed #EF4444' }} /> Target
              </span>
            )}
          </div>
        </CardContent>
      </Card>

      {/* Total Sales Value Chart */}
      <Card className="shadow-sm border border-slate-200 rounded-2xl overflow-hidden">
        <CardHeader className="border-b bg-gradient-to-r from-amber-50/80 to-white py-4">
          <CardTitle className="flex items-center gap-2 text-base font-semibold">
            <Target className="h-5 w-5 text-amber-600" />
            Total Sales Value (Monthly)
            <Badge variant="secondary" className="ml-auto text-xs bg-amber-100 text-amber-700 font-normal">
              Contract Values Created
            </Badge>
          </CardTitle>
        </CardHeader>
        <CardContent className="p-4 pt-6">
          <ResponsiveContainer width="100%" height={280}>
            <ComposedChart 
              data={data.sales_value} 
              margin={{ left: 25, right: 20, top: 20, bottom: 20 }}
            >
              <CartesianGrid strokeDasharray="3 3" stroke="#f1f5f9" strokeOpacity={0.8} />
              <XAxis 
                dataKey="label" 
                tick={{ fontSize: 11, fill: '#64748b' }} 
                tickLine={false}
                axisLine={{ stroke: '#e2e8f0' }}
              />
              <YAxis 
                tick={{ fontSize: 11, fill: '#64748b' }} 
                tickFormatter={(v) => formatCurrency(v)}
                tickLine={false}
                axisLine={false}
                width={55}
              />
              <Tooltip content={<CustomTooltip isCurrency />} />
              <Bar 
                dataKey="value" 
                fill="#F59E0B" 
                radius={[6, 6, 0, 0]}
                cursor="pointer"
                onClick={(data) => data && openDrilldown('sales', data.month)}
                name="$ Sales Value"
              />
              {data.sales_value?.some(d => d.target) && (
                <Line 
                  type="monotone" 
                  dataKey="target" 
                  stroke="#EF4444" 
                  strokeWidth={2}
                  strokeDasharray="5 5"
                  dot={false}
                  name="Target"
                />
              )}
            </ComposedChart>
          </ResponsiveContainer>
          <div className="flex items-center justify-center gap-6 mt-2 text-xs text-slate-500">
            <span className="flex items-center gap-1.5">
              <span className="w-3 h-3 rounded bg-amber-500" /> Sales Value
            </span>
            {data.sales_value?.some(d => d.target) && (
              <span className="flex items-center gap-1.5">
                <span className="w-4 h-0.5 bg-red-500" style={{ borderTop: '2px dashed #EF4444' }} /> Target
              </span>
            )}
          </div>
        </CardContent>
      </Card>

      {/* Drilldown Modal */}
      <DrilldownModal 
        isOpen={drilldown.open}
        onClose={() => setDrilldown({ open: false, type: '', month: '', data: null })}
        title={
          drilldown.type === 'conversions' ? 'Client Conversions' :
          drilldown.type === 'cash' ? 'Cash Collection' :
          'Sales Value'
        }
        data={drilldown.data}
        type={drilldown.type}
        month={drilldown.month}
      />

      {/* Target Settings Modal */}
      <TargetSettingsModal 
        isOpen={showTargetSettings}
        onClose={() => setShowTargetSettings(false)}
        onSaved={fetchData}
      />
    </div>
  );
}

// Installment Performance Chart for Intelligence Dashboard
export function InstallmentPerformanceChart() {
  const { role } = usePermissions();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [drilldown, setDrilldown] = useState({ open: false, type: '', month: '', data: null });
  const [showTargetSettings, setShowTargetSettings] = useState(false);

  const canSetTargets = ['admin', 'owner', 'top_management'].includes(role);

  const fetchData = useCallback(async () => {
    try {
      const response = await fetch(`${API_URL}/api/analytics/installment-performance?months=12`, {
        credentials: 'include'
      });
      if (response.ok) {
        const result = await response.json();
        setData(result);
      }
    } catch (error) {
      logger.error('Error fetching installment performance:', error);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchData();
    // Real-time refresh every 30 seconds
    const interval = setInterval(fetchData, 30000);
    return () => clearInterval(interval);
  }, [fetchData]); // eslint-disable-line react-hooks/exhaustive-deps

  const openDrilldown = async (type, month) => {
    try {
      const endpoint = type === 'expected' 
        ? `/api/analytics/installment-drilldown/expected/${month}`
        : `/api/analytics/installment-drilldown/collected/${month}`;

      const response = await fetch(`${API_URL}${endpoint}`, { credentials: 'include' });
      if (response.ok) {
        const result = await response.json();
        setDrilldown({ open: true, type, month, data: result });
      }
    } catch (error) {
      logger.error('Error fetching drilldown:', error);
    }
  };

  if (loading) {
    return (
      <Card className="shadow-sm">
        <CardContent className="p-6">
          <Skeleton className="h-8 w-48 mb-4" />
          <div className="grid grid-cols-4 gap-4 mb-6">
            {[1, 2, 3, 4].map(i => <Skeleton key={i} className="h-24 rounded-xl" />)}
          </div>
          <Skeleton className="h-80 rounded-xl" />
        </CardContent>
      </Card>
    );
  }

  if (!data) return null;

  const { kpis, chart_data } = data;

  return (
    <Card className="shadow-sm border border-slate-200 rounded-2xl overflow-hidden" data-testid="installment-performance-chart">
      <CardHeader className="border-b bg-gradient-to-r from-purple-50/80 to-white py-4">
        <div className="flex items-center justify-between">
          <CardTitle className="flex items-center gap-2 text-base font-semibold">
            <TrendingUp className="h-5 w-5 text-purple-600" />
            Installments: Expected vs Collected
            <Badge variant="outline" className="ml-2 text-xs font-normal">Real-time • Excludes Booking/Downpayment</Badge>
          </CardTitle>
          <div className="flex items-center gap-2">
            {canSetTargets && (
              <Button 
                variant="ghost" 
                size="sm" 
                onClick={() => setShowTargetSettings(true)}
                className="text-slate-500"
              >
                <Settings size={14} className="mr-1" /> Targets
              </Button>
            )}
            <Button variant="ghost" size="sm" onClick={fetchData} className="text-slate-500">
              <RefreshCw size={14} className="mr-1" /> Refresh
            </Button>
          </div>
        </div>
      </CardHeader>
      <CardContent className="p-4 pt-6">
        {/* KPI Tiles */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-6">
          <ExecKPITile 
            title="Expected This Month"
            value={formatCurrency(kpis.expected_this_month)}
            subtitle={kpis.target_this_month ? `Target: ${formatCurrency(kpis.target_this_month)}` : undefined}
            icon={Target}
            color="amber"
          />
          <ExecKPITile 
            title="Collected This Month"
            value={formatCurrency(kpis.collected_this_month)}
            icon={DollarSign}
            color="green"
          />
          <ExecKPITile 
            title="Collection Rate"
            value={`${kpis.collection_rate || 0}%`}
            icon={TrendingUp}
            color={kpis.collection_rate >= 80 ? 'green' : kpis.collection_rate >= 50 ? 'amber' : 'red'}
          />
          <ExecKPITile 
            title="Overdue Amount"
            value={formatCurrency(kpis.overdue_amount)}
            subtitle={`${kpis.overdue_count || 0} installments`}
            icon={AlertTriangle}
            color="red"
          />
        </div>

        {/* Chart */}
        <ResponsiveContainer width="100%" height={320}>
          <ComposedChart 
            data={chart_data} 
            margin={{ left: 25, right: 20, top: 20, bottom: 20 }}
          >
            <CartesianGrid strokeDasharray="3 3" stroke="#f1f5f9" strokeOpacity={0.8} />
            <XAxis 
              dataKey="label" 
              tick={{ fontSize: 11, fill: '#64748b' }} 
              tickLine={false}
              axisLine={{ stroke: '#e2e8f0' }}
            />
            <YAxis 
              tick={{ fontSize: 11, fill: '#64748b' }} 
              tickFormatter={(v) => formatCurrency(v)}
              tickLine={false}
              axisLine={false}
              width={55}
            />
            <Tooltip 
              content={({ active, payload, label }) => {
                if (active && payload && payload.length) {
                  const d = payload[0].payload;
                  return (
                    <div className="bg-white p-4 rounded-xl shadow-xl border border-slate-100">
                      <p className="font-semibold text-slate-900 mb-2">{label}</p>
                      <div className="space-y-1.5 text-sm">
                        <p className="text-amber-600 flex justify-between gap-4">
                          <span>Expected:</span> <span className="font-semibold">{formatCurrency(d.expected, false)}</span>
                        </p>
                        <p className="text-emerald-600 flex justify-between gap-4">
                          <span>Collected:</span> <span className="font-semibold">{formatCurrency(d.collected, false)}</span>
                        </p>
                        {d.target && (
                          <p className="text-red-600 flex justify-between gap-4 border-t pt-1.5">
                            <span>Target:</span> <span className="font-semibold">{formatCurrency(d.target, false)}</span>
                          </p>
                        )}
                        <p className="text-slate-500 flex justify-between gap-4 border-t pt-1.5">
                          <span>Gap:</span> <span className="font-semibold">{formatCurrency(d.gap, false)}</span>
                        </p>
                        <p className="text-purple-600 flex justify-between gap-4">
                          <span>Rate:</span> <span className="font-bold">{d.collection_rate}%</span>
                        </p>
                      </div>
                    </div>
                  );
                }
                return null;
              }}
            />
            <Legend 
              wrapperStyle={{ paddingTop: '16px' }}
              iconType="circle"
              iconSize={8}
            />
            <Bar 
              dataKey="expected" 
              fill="#F59E0B" 
              radius={[6, 6, 0, 0]}
              name="Expected"
              cursor="pointer"
              onClick={(data) => data && openDrilldown('expected', data.month)}
            />
            <Bar 
              dataKey="collected" 
              fill="#10B981" 
              radius={[6, 6, 0, 0]}
              name="Collected"
              cursor="pointer"
              onClick={(data) => data && openDrilldown('collected', data.month)}
            />
            {chart_data?.some(d => d.target) && (
              <Line 
                type="monotone" 
                dataKey="target" 
                stroke="#EF4444" 
                strokeWidth={2}
                strokeDasharray="5 5"
                dot={false}
                name="Target"
              />
            )}
          </ComposedChart>
        </ResponsiveContainer>
        
        <p className="text-xs text-slate-400 text-center mt-4">
          Click bars for detailed breakdown • Installments only (excludes booking & downpayment)
        </p>
      </CardContent>

      {/* Drilldown Modal */}
      <DrilldownModal 
        isOpen={drilldown.open}
        onClose={() => setDrilldown({ open: false, type: '', month: '', data: null })}
        title={drilldown.type === 'expected' ? 'Expected Installments' : 'Collected Installments'}
        data={drilldown.data}
        type={drilldown.type}
        month={drilldown.month}
      />

      {/* Target Settings Modal */}
      <TargetSettingsModal 
        isOpen={showTargetSettings}
        onClose={() => setShowTargetSettings(false)}
        onSaved={fetchData}
      />
    </Card>
  );
}

export default ExecutiveDashboardCharts;
